import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";

/**
 * Emergent-managed Google OAuth landing page.
 * The auth provider redirects users back to
 *   <our origin>/dashboard#session_id=<id>
 * This component is mounted by App.js BEFORE the normal <Routes> tree
 * whenever `window.location.hash` contains `session_id=`. We exchange the
 * session_id for our JWT, store it, strip the hash, and navigate the user to
 * the appropriate landing page.
 *
 * Uses a useRef guard so React StrictMode double-mounts don't trigger two
 * exchange requests (session_ids are single-use).
 *
 * REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS,
 * THIS BREAKS THE AUTH.
 */
export default function AuthCallback() {
  const { googleComplete } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const processed = useRef(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (processed.current) return;
    processed.current = true;

    const hash = window.location.hash || "";
    const match = hash.match(/session_id=([^&]+)/);
    const sessionId = match ? decodeURIComponent(match[1]) : null;
    // Strip the hash so the token never lingers in browser history.
    if (window.history?.replaceState) {
      window.history.replaceState(null, "", window.location.pathname + window.location.search);
    }

    if (!sessionId) {
      setError("Missing session - please try signing in again.");
      return;
    }

    googleComplete(sessionId)
      .then((u) => {
        toast.success("Signed in with Google.");
        navigate(u.onboarded ? "/dashboard" : "/onboarding", { replace: true });
      })
      .catch((err) => {
        const detail = err?.response?.data?.detail;
        setError(typeof detail === "string" ? detail : "Google sign-in failed.");
      });
  }, [googleComplete, navigate, location]);

  return (
    <div className="min-h-screen flex items-center justify-center px-6" data-testid="auth-callback-screen">
      <div className="text-center max-w-sm">
        {error ? (
          <>
            <div className="terminal-label text-primary mb-3">[ ERROR ]</div>
            <p className="text-sm text-foreground/80 leading-relaxed mb-5">{error}</p>
            <button onClick={() => navigate("/auth", { replace: true })} className="btn-primary" data-testid="auth-callback-retry">
              Back to sign in →
            </button>
          </>
        ) : (
          <>
            <div className="terminal-label mb-3">SIGNING.YOU.IN</div>
            <div className="text-xs text-muted-foreground blink-caret">Connecting your Google account</div>
          </>
        )}
      </div>
    </div>
  );
}
